import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import textToSpeechPlayerData from '../data/components/text-to-speech-player.json';

interface TextSegment {
  text: string;
  element: HTMLElement;
  index: number;
}

const PlayerContainer = styled(motion.div)<{ colors: any }>`
  position: fixed;
  bottom: 80px;
  right: 20px;
  width: 380px;
  max-width: calc(100vw - 40px);
  background: ${props => props.colors.bgCard};
  border: 2px solid ${props => props.colors.primary};
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  z-index: 99999;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: calc(100vw - 40px);
    bottom: 120px;
    z-index: 99999;
  }
`;

const PlayerHeader = styled.div<{ colors: any }>`
  background: ${props => props.colors.primary};
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PlayerTitle = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1.5rem;
  font-weight: bold;
  z-index: 100000;
  pointer-events: auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    padding: 6px 10px;
  }
`;

const PlayerBody = styled.div`
  padding: 16px;
`;

const CurrentText = styled.div<{ colors: any }>`
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  color: #111827;
  line-height: 1.5;
  max-height: calc(0.85rem * 1.5 * 5);
  overflow-y: auto;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid ${props => props.colors.primary};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ProgressContainer = styled.div`
  margin-bottom: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  
  &:focus-visible {
    outline: 3px solid #00d9ff;
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(0, 217, 255, 0.3), 0 0 0 6px rgba(255, 255, 255, 0.8);
  }
`;

const ProgressFill = styled.div<{ progress: number; colors: any }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => props.colors.primary};
  transition: width 0.1s linear;
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 4px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ControlButton = styled.button<{ size?: 'small' | 'large'; colors: any }>`
  width: ${props => props.size === 'large' ? '48px' : '36px'};
  height: ${props => props.size === 'large' ? '48px' : '36px'};
  border-radius: 50%;
  background-color: ${props => props.size === 'large' ? props.colors.primary : '#f3f4f6'} !important;
  background: ${props => props.size === 'large' ? props.colors.primary : '#f3f4f6'} !important;
  color: ${props => props.size === 'large' ? 'white' : '#6b7280'} !important;
  border: none !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: ${props => props.size === 'large' ? '1.2rem' : '1rem'};
  outline: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
  -webkit-tap-highlight-color: transparent !important;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0) !important;
  -webkit-focus-ring-color: transparent !important;
  box-shadow: none !important;
  position: relative;
  overflow: hidden;
  
  /* Entferne alle blauen Browser-Standard-Farben */
  &::-moz-focus-inner {
    border: 0 !important;
    outline: none !important;
    background-color: transparent !important;
  }
  
  /* Stelle sicher, dass der Inhalt keine blauen Farben hat */
  * {
    background-color: transparent !important;
    color: inherit !important;
  }
  
  &:hover {
    background-color: ${props => props.size === 'large' ? props.colors.primaryHover : '#e5e7eb'} !important;
    background: ${props => props.size === 'large' ? props.colors.primaryHover : '#e5e7eb'} !important;
    box-shadow: none !important;
  }
  
  &:focus:not(:focus-visible) {
    outline: none !important;
    box-shadow: none !important;
  }
  
  /* Use global focus styles for keyboard navigation */
  &:focus-visible {
    /* Let global styles apply - they use türkis (#00d9ff) */
    outline: 4px solid #00d9ff !important;
    outline-offset: 2px !important;
    box-shadow: 0 0 0 4px rgba(0, 217, 255, 0.4), 0 0 0 8px rgba(255, 255, 255, 0.9) !important;
  }
  
  &:active {
    outline: none !important;
    box-shadow: none !important;
    background-color: ${props => props.size === 'large' ? props.colors.primaryHover : '#d1d5db'} !important;
    background: ${props => props.size === 'large' ? props.colors.primaryHover : '#d1d5db'} !important;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SettingLabel = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 80px;
`;

const Slider = styled.input<{ colors: any }>`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${props => props.colors.primary};
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${props => props.colors.primary};
    cursor: pointer;
    border: none;
  }
`;

const SettingValue = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  color: #111827;
  min-width: 40px;
  text-align: right;
`;

const VoiceSelect = styled.select<{ colors: any }>`
  flex: 1;
  padding: 6px 10px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.8rem;
  color: #111827;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.colors.primary};
  }
`;

interface TextToSpeechPlayerProps {
  isActive: boolean;
  onClose: () => void;
  targetElement?: HTMLElement | null; // Optional: Nur bestimmtes Element vorlesen (z.B. Cookie-Popup)
}

const TextToSpeechPlayer: React.FC<TextToSpeechPlayerProps> = ({ isActive, onClose, targetElement }) => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [isPlaying, setIsPlaying] = useState(false);
  const [segments, setSegments] = useState<TextSegment[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<'de' | 'en'>('de');
  
  // Read language from localStorage (managed by Layout)
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Listen for language changes from Layout
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasPlayingRef = useRef<boolean>(false);
  const progressAtPauseRef = useRef<number>(0);
  const playerContainerRef = useRef<HTMLDivElement>(null);

  // Detect current language from HTML or localStorage
  useEffect(() => {
    const detectLanguage = () => {
      const htmlLang = document.documentElement.getAttribute('lang');
      const storedLang = localStorage.getItem('siteLanguage');
      const detectedLang = (htmlLang || storedLang || 'de') as 'de' | 'en';
      setCurrentLanguage(detectedLang);
      console.log(`🌍 Player erkannte Sprache: ${detectedLang}`);
    };

    detectLanguage();

    // Listen for language changes
    const observer = new MutationObserver(() => {
      detectLanguage();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang']
    });

    return () => observer.disconnect();
  }, []);

  // Load available voices based on current language
  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      
      // Filter voices based on current language
      const langPrefix = currentLanguage === 'de' ? 'de' : 'en';
      const filteredVoices = availableVoices
        .filter(voice => voice.lang.startsWith(langPrefix))
        .sort((a, b) => {
          // Google voices first
          if (a.name.includes('Google') && !b.name.includes('Google')) return -1;
          if (!a.name.includes('Google') && b.name.includes('Google')) return 1;
          // Then by quality indicators
          if (a.name.includes('Premium')) return -1;
          if (b.name.includes('Premium')) return 1;
          return 0;
        });
      
      console.log(`🎤 ${filteredVoices.length} ${currentLanguage === 'de' ? 'deutsche' : 'englische'} Stimmen gefunden`);
      setVoices(filteredVoices);
      
      // Auto-select first voice if none selected or language changed
      if (filteredVoices.length > 0) {
        setSelectedVoice(filteredVoices[0]);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [currentLanguage]);

  // Extract text segments from page - also re-extract when language changes!
  useEffect(() => {
    if (isActive) {
      const extractSegments = () => {
        let allElements: HTMLElement[] = [];
        
        // Wenn targetElement gesetzt ist (z.B. Cookie-Popup, AccessibilityPanel), nur dieses Element vorlesen
        if (targetElement) {
          // Suche nach allen Text-Elementen, einschließlich divs mit Text-Inhalt
          // Erweitere Suche um alle möglichen Text-Container
          allElements = Array.from(targetElement.querySelectorAll('*')) as HTMLElement[];
          // Filtere nur Elemente mit Text-Inhalt heraus
          allElements = allElements.filter(el => {
            const text = el.textContent?.trim() || '';
            const hasText = text.length > 0;
            if (!hasText) return false;
            
            // Prüfe ob Element sichtbar ist
            const style = window.getComputedStyle(el);
            const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && parseFloat(style.opacity) > 0;
            if (!isVisible) return false;
            
            const tagName = el.tagName.toLowerCase();
            
            // Standard Text-Elemente immer einbeziehen
            if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tagName)) {
              return true;
            }
            
            // Für divs/span: nur wenn sie direkten Text haben oder spezielle Container sind
            if (tagName === 'div' || tagName === 'span') {
              // Prüfe ob direkter Text vorhanden ist
              const directText = Array.from(el.childNodes).some(node => 
                node.nodeType === Node.TEXT_NODE && (node.textContent?.trim().length ?? 0) > 0
              );
              
              // Prüfe ob es ein Text-Container ist (durch Daten-Attribute oder spezielle Struktur)
              const isTextContainer = 
                el.getAttribute('role') === 'heading' ||
                el.getAttribute('data-text-element') === 'true' ||
                // Prüfe ob Element nur Text-Kinder hat (keine verschachtelten Container)
                (el.children.length === 0 && directText) ||
                // Prüfe ob alle Kinder Text-Elemente sind
                (el.children.length > 0 && Array.from(el.children).every(child => 
                  ['SPAN', 'STRONG', 'EM', 'B', 'I', 'U'].includes(child.tagName)
                ));
              
              return directText || isTextContainer;
            }
            
            return false;
          });
        } else {
          // Normale Logik: Suche im gesamten body
          const body = document.body;
          
          // Finde alle Text-Elemente im body
          // Erweitere Suche um divs mit data-text-element Attribut
          const allTextElements = Array.from(body.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, [data-text-element="true"]')) as HTMLElement[];
          
          // Filtere Overlays komplett aus - nur normale Seiten-Elemente verwenden
          const normalElements: HTMLElement[] = [];
          
          allTextElements.forEach(element => {
            // Prüfe ob Element in einem Overlay-Container ist (fixed/absolute mit hohem z-index)
            let parent = element.parentElement;
            let isInOverlay = false;
            
            while (parent && parent !== body) {
              const parentStyle = window.getComputedStyle(parent);
              const parentPosition = parentStyle.position;
              const parentZIndex = parseInt(parentStyle.zIndex) || 0;
              
              // Overlay erkannt: fixed/absolute mit z-index >= 10000 (Cookie-Popup, Modals, AccessibilityPanel, etc.)
              if ((parentPosition === 'fixed' || parentPosition === 'absolute') && parentZIndex >= 10000) {
                // Prüfe ob Overlay wirklich sichtbar ist - STRENGE Prüfungen
                const overlayStyle = window.getComputedStyle(parent);
                const isDisplayed = overlayStyle.display !== 'none';
                const isVisible = parseFloat(overlayStyle.opacity) > 0.5; // Mindestens 50% opacity (strenger!)
                const isNotHidden = overlayStyle.visibility !== 'hidden';
                
                // Zusätzlich: Prüfe ob Element wirklich im Viewport ist (getBoundingClientRect)
                const rect = parent.getBoundingClientRect();
                const isInViewport = rect.width > 10 && rect.height > 10; // Mindestens 10px groß
                
                // Prüfe ob es ein AnimatePresence-Exit-Element ist (durch Framer Motion versteckt)
                // AnimatePresence entfernt Elemente nach Exit-Animation, aber während der Animation sind sie noch da
                const isAnimatePresenceExit = parent.hasAttribute('data-exit') || 
                  (parent.parentElement && window.getComputedStyle(parent.parentElement).display === 'none');
                
                // Prüfe ob das Overlay wirklich gerendert und sichtbar ist
                // WICHTIG: Nur wenn ALLE Bedingungen erfüllt sind, ist es ein sichtbares Overlay
                if (isDisplayed && isVisible && isNotHidden && isInViewport && !isAnimatePresenceExit) {
                  isInOverlay = true;
                  break;
                }
              }
              parent = parent.parentElement;
            }
            
            // NUR normale Elemente hinzufügen - Overlays komplett ignorieren
            if (!isInOverlay) {
              normalElements.push(element);
            }
          });
          
          // Nur normale Elemente verwenden - Overlays komplett ausschließen
          allElements = normalElements;
        }
        
        const textSegments: TextSegment[] = [];
        const processedElements = new Set<HTMLElement>();
        
        // Process elements in DOM order
        allElements.forEach((el) => {
          const element = el as HTMLElement;
          const tagName = element.tagName.toLowerCase();
          
          // Skip if already processed
          if (processedElements.has(element)) {
            return;
          }
          
          // Process direct text elements (p, h1-h6, li)
          const isTextElement = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(tagName);
          
          // For divs/span: process if they contain text and don't have complex nested structure
          let isTextContainer = false;
          if (tagName === 'div' || tagName === 'span') {
            // Prüfe ob Element nur Text oder einfache Formatierungs-Elemente enthält
            const hasOnlyTextChildren = Array.from(element.children).every(child => {
              const childTag = child.tagName.toLowerCase();
              // Erlaube nur Formatierungs-Elemente (span, strong, em, etc.) und Links
              return ['span', 'strong', 'em', 'b', 'i', 'u', 'mark', 'a'].includes(childTag);
            });
            
            // Oder wenn es keine Kinder-Elemente hat (nur Text-Node)
            const hasNoComplexChildren = element.children.length === 0;
            
            // Oder wenn es ein spezielles Text-Element ist (durch role oder data-Attribute)
            const isSpecialTextElement = 
              element.getAttribute('role') === 'heading' ||
              element.getAttribute('data-text-element') === 'true';
            
            // NEU: Prüfe ob Element hauptsächlich Text enthält, auch wenn es verschachtelte Strukturen hat
            // (z.B. Buttons, Links innerhalb von Paragraphs)
            const textContent = element.textContent?.trim() || '';
            const hasSubstantialText = textContent.length > 20; // Mindestens 20 Zeichen Text
            
            // Prüfe ob Element hauptsächlich Text enthält (nicht nur Buttons/Links)
            // Zähle Text-Nodes vs. komplexe Elemente
            let textNodeCount = 0;
            let complexElementCount = 0;
            
            const countNodes = (node: Node) => {
              if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim().length) {
                textNodeCount++;
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                const tag = el.tagName.toLowerCase();
                // Formatierungs-Elemente und Links zählen als Text
                if (['span', 'strong', 'em', 'b', 'i', 'u', 'mark', 'a'].includes(tag)) {
                  // Rekursiv prüfen
                  Array.from(el.childNodes).forEach(countNodes);
                } else if (['button', 'input', 'select', 'textarea'].includes(tag)) {
                  // Buttons/Inputs zählen als komplex, aber wenn sie nur kleine Teile sind, ist es ok
                  complexElementCount++;
                } else {
                  // Andere Elemente zählen als komplex
                  complexElementCount++;
                }
              }
            };
            
            Array.from(element.childNodes).forEach(countNodes);
            
            // Element ist hauptsächlich Text, wenn:
            // - Es hat substantiellen Text-Inhalt UND
            // - Text-Nodes überwiegen oder komplexe Elemente sind nur kleine Teile (z.B. Buttons)
            const isPrimarilyText = hasSubstantialText && (
              textNodeCount > complexElementCount || 
              (complexElementCount > 0 && textContent.length > 100) // Bei langem Text sind einige Buttons ok
            );
            
            isTextContainer = hasOnlyTextChildren || hasNoComplexChildren || isSpecialTextElement || isPrimarilyText;
          }
          
          if (!isTextElement && !isTextContainer) {
            return;
          }
          
          // Skip if this element is a child of an already processed text element
          // (to avoid reading nested text elements)
          let isChildOfProcessed = false;
          for (const processedEl of processedElements) {
            if (processedEl !== element && processedEl.contains(element)) {
              const processedTag = processedEl.tagName.toLowerCase();
              const isProcessedTextElement = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li'].includes(processedTag);
              const isProcessedTextContainer = (processedTag === 'div' || processedTag === 'span') && (
                processedEl.className.includes('SettingLabel') ||
                processedEl.className.includes('SettingDescription') ||
                processedEl.className.includes('CategoryTitle') ||
                processedEl.className.includes('CategoryDescription')
              );
              if (isProcessedTextElement || isProcessedTextContainer) {
                isChildOfProcessed = true;
                break;
              }
            }
          }
          if (isChildOfProcessed) {
            return;
          }
            
          // Get text content - use textContent instead of innerText to avoid container text
          const text = element.textContent?.trim() || '';
          
          // Only add if text exists
          if (text && text.length > 0) {
            textSegments.push({
              text,
              element: element,
              index: textSegments.length
            });
            processedElements.add(element);
          }
        });
        
        setSegments(textSegments);
        setCurrentIndex(0);
        console.log(`📖 ${textSegments.length} Text-Abschnitte gefunden (${currentLanguage === 'de' ? 'Deutsch' : 'English'})`);
        console.log('📋 Alle Segmente:', textSegments.map((s, i) => `${i + 1}. [${s.element.tagName}] "${s.text.substring(0, 60)}${s.text.length > 60 ? '...' : ''}"`).join('\n'));
      };
      
      // Small delay to ensure DOM has updated with new language
      setTimeout(() => {
        extractSegments();
      }, 100);
      
      // Stop any ongoing speech when language changes
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, currentLanguage]); // ← WICHTIG: currentLanguage hinzugefügt!

  // Live-Update: Wenn rate, volume oder voice sich ändern während des Abspielens
  useEffect(() => {
    // Nur wenn gerade etwas abgespielt wird
    if (isPlaying && segments.length > 0 && currentIndex < segments.length) {
      console.log('🔄 Einstellungen geändert, lese ab ~' + Math.round(progress) + '% weiter...');
      
      // Aktuelle Progress Position speichern
      progressAtPauseRef.current = progress;
      wasPlayingRef.current = true;
      
      // Stoppe aktuelles Vorlesen
      window.speechSynthesis.cancel();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // Kurze Verzögerung damit cancel durchgeht
      setTimeout(() => {
        if (wasPlayingRef.current && segments[currentIndex]) {
          // Berechne wo im Text wir ungefähr sind (basierend auf Progress)
          const fullText = segments[currentIndex].text;
          const progressPercent = progressAtPauseRef.current / 100;
          const estimatedCharPosition = Math.floor(fullText.length * progressPercent);
          
          // Finde den nächsten Satzanfang nach dieser Position
          let startPosition = estimatedCharPosition;
          const nextSentence = fullText.substring(startPosition);
          
          // Suche nach Satz-/Wortgrenze (Leerzeichen, Punkt, Komma)
          const boundaryMatch = nextSentence.match(/[\s.,;!?]/);
          if (boundaryMatch && boundaryMatch.index !== undefined && boundaryMatch.index < 50) {
            startPosition += boundaryMatch.index + 1;
          }
          
          const remainingText = fullText.substring(startPosition);
          
          if (remainingText.length > 20) {
            console.log(`▶️ Starte bei ~${Math.round(progressPercent * 100)}% (Char ${startPosition}/${fullText.length})`);
            speakFromPosition(remainingText, currentIndex, startPosition, fullText.length);
          } else {
            // Zu wenig Text übrig, nächstes Segment
            if (currentIndex < segments.length - 1) {
              playSegment(currentIndex + 1);
            }
          }
          wasPlayingRef.current = false;
        }
      }, 150);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate, volume, selectedVoice]);

  const speak = (text: string, index: number, startProgress: number = 0) => {
    // Stop any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    // Set language dynamically based on current language
    utterance.lang = currentLanguage === 'de' ? 'de-DE' : 'en-US';
    utterance.rate = rate;
    utterance.pitch = 1.0;
    utterance.volume = volume;
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
    
      utterance.onstart = () => {
      setIsPlaying(true);
      setProgress(startProgress);
      // Highlight current element and scroll to it
      const currentSegment = segments[index];
      if (currentSegment) {
        // Remove highlight from previous element
        segments.forEach((seg, idx) => {
          if (idx !== index) {
            // Entferne alle Highlight-Styles
            seg.element.style.backgroundColor = '';
            seg.element.style.outline = '';
            seg.element.style.outlineOffset = '';
            seg.element.style.boxShadow = '';
          }
        });
        
        // Prüfe ob Element in einem Popup/Overlay ist
        let parent = currentSegment.element.parentElement;
        let popupContainer: HTMLElement | null = null;
        
        while (parent && parent !== document.body) {
          const parentStyle = window.getComputedStyle(parent);
          const parentPosition = parentStyle.position;
          const parentZIndex = parseInt(parentStyle.zIndex) || 0;
          
          // Popup/Overlay erkannt: fixed/absolute mit z-index >= 10000
          if ((parentPosition === 'fixed' || parentPosition === 'absolute') && parentZIndex >= 10000) {
            // Prüfe ob es ein scrollbarer Container ist
            const hasOverflow = parentStyle.overflow === 'auto' || parentStyle.overflow === 'scroll' || 
                               parentStyle.overflowY === 'auto' || parentStyle.overflowY === 'scroll';
            
            if (hasOverflow) {
              popupContainer = parent;
              break;
            } else {
              // Auch wenn kein overflow, könnte es ein Popup-Container sein
              // Suche nach dem nächsthöheren scrollbaren Container
              popupContainer = parent;
            }
          }
          parent = parent.parentElement;
        }
        
        if (popupContainer) {
          // Element ist in einem Popup - scroll innerhalb des Popup-Containers
          const containerRect = popupContainer.getBoundingClientRect();
          const elementRect = currentSegment.element.getBoundingClientRect();
          
          // Berechne relative Position im Container
          const elementTopRelative = elementRect.top - containerRect.top + popupContainer.scrollTop;
          const elementBottomRelative = elementTopRelative + elementRect.height;
          
          // Prüfe ob Element sichtbar ist
          const containerScrollTop = popupContainer.scrollTop;
          const containerHeight = popupContainer.clientHeight;
          const visibleTop = containerScrollTop;
          const visibleBottom = containerScrollTop + containerHeight;
          
          // Scroll nur wenn Element nicht vollständig sichtbar ist
          if (elementTopRelative < visibleTop) {
            // Element ist oberhalb des sichtbaren Bereichs
            popupContainer.scrollTo({
              top: elementTopRelative - 20, // 20px Offset oben
              behavior: 'smooth'
            });
          } else if (elementBottomRelative > visibleBottom) {
            // Element ist unterhalb des sichtbaren Bereichs
            popupContainer.scrollTo({
              top: elementBottomRelative - containerHeight + 20, // 20px Offset unten
              behavior: 'smooth'
            });
          }
          
          // Fallback: scrollIntoView für Popups
          currentSegment.element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        } else {
          // Normale Seite - scroll im Fenster
          const rect = currentSegment.element.getBoundingClientRect();
          const elementTop = rect.top + window.pageYOffset;
          const offset = 120; // Offset from top to account for header
          
          window.scrollTo({
            top: Math.max(0, elementTop - offset),
            behavior: 'smooth'
          });
        }
        
        // Highlight current element - verwende türkisen Rahmen statt Hintergrund
        // für konsistente Sichtbarkeit auf jedem Hintergrund
        currentSegment.element.style.outline = '3px solid #00d9ff';
        currentSegment.element.style.outlineOffset = '2px';
        currentSegment.element.style.boxShadow = '0 0 0 4px rgba(0, 217, 255, 0.3)';
      }
      
      // Progress update
      const textLength = text.length;
      const estimatedDuration = (textLength / 15) * (1 / rate) * 1000; // Rough estimate
      const updateInterval = 100;
      const progressRange = 100 - startProgress; // Verbleibender Progress
      
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          const increment = (updateInterval / estimatedDuration) * progressRange;
          const newProgress = prev + increment;
          return Math.min(newProgress, 100);
        });
      }, updateInterval);
    };
    
    utterance.onend = () => {
      setIsPlaying(false);
      setProgress(100);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Remove highlight
      const currentSegment = segments[index];
      if (currentSegment) {
        currentSegment.element.style.backgroundColor = '';
        currentSegment.element.style.outline = '';
        currentSegment.element.style.outlineOffset = '';
        currentSegment.element.style.boxShadow = '';
      }
      
      // Auto-play next segment
      if (index < segments.length - 1) {
        // Längere Pause für Cookie-Details (erkennbar an kurzen Texten mit "Läuft ab", "Aktiv", etc.)
        const currentText = segments[index]?.text || '';
        const isCookieDetail = currentText.length < 100 && (
          currentText.includes('Läuft ab') || 
          currentText.includes('Aktiv') || 
          currentText.includes('Akzeptiert') ||
          currentText.includes('Expires') ||
          currentText.includes('Active') ||
          currentText.includes('Accepted')
        );
        const pauseDuration = isCookieDetail ? 800 : 500;
        
        setTimeout(() => {
          playSegment(index + 1);
        }, pauseDuration);
      }
    };
    
    utterance.onerror = () => {
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  const speakFromPosition = (text: string, index: number, startCharPos: number, totalTextLength: number) => {
    // Berechne Progress basierend auf Character-Position
    const startProgressPercent = (startCharPos / totalTextLength) * 100;
    
    // Nutze die normale speak-Funktion mit startProgress
    speak(text, index, startProgressPercent);
  };

  const playSegment = (index: number) => {
    if (index >= 0 && index < segments.length) {
      setCurrentIndex(index);
      speak(segments[index].text, index);
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      window.speechSynthesis.pause();
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    } else if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPlaying(true);
    } else {
      playSegment(currentIndex);
    }
  };

  const playNext = () => {
    if (currentIndex < segments.length - 1) {
      playSegment(currentIndex + 1);
    }
  };

  const playPrevious = () => {
    if (currentIndex > 0) {
      playSegment(currentIndex - 1);
    }
  };

  const handleClose = React.useCallback(() => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    // Remove all highlights
    segments.forEach(seg => {
      seg.element.style.backgroundColor = '';
      seg.element.style.outline = '';
      seg.element.style.outlineOffset = '';
      seg.element.style.boxShadow = '';
    });
    onClose();
  }, [segments, onClose]);

  // Get all focusable elements within the player
  const getFocusableElements = React.useCallback(() => {
    if (!playerContainerRef.current) return [];
    
    const focusableSelectors = [
      'input:not([disabled]):not([type="hidden"])',
      'button:not([disabled])',
      'a[href]',
      'textarea:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');
    
    const allElements = Array.from(
      playerContainerRef.current.querySelectorAll<HTMLElement>(focusableSelectors)
    );
    
    return allElements.filter(el => {
      // Skip disabled buttons (they're not focusable)
      if (el.hasAttribute('disabled') && el.tagName === 'BUTTON') {
        return false;
      }
      
      const style = window.getComputedStyle(el);
      const isVisible = style.display !== 'none' && style.visibility !== 'hidden';
      // Buttons can have lower opacity when disabled, but we already filtered those out
      // For other elements, check opacity
      const hasOpacity = el.tagName === 'BUTTON' || style.opacity !== '0';
      return isVisible && hasOpacity;
    });
  }, []);

  // Focus Trap für TextToSpeechPlayer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive || !playerContainerRef.current) return;

      // ESC-Taste zum Schließen (prioritär)
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Remove focus before closing
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        handleClose();
        return;
      }

      // Focus Trap: Keep Tab navigation within the player
      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        
        if (focusableElements.length === 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement as HTMLElement;

        const isWithinPlayer = playerContainerRef.current.contains(activeElement);

        // If focus is outside player, focus first element
        if (!isWithinPlayer) {
          e.preventDefault();
          e.stopPropagation();
          // Verzögere Focus, um sicherzustellen, dass andere Event-Handler nicht interferieren
          setTimeout(() => {
            firstElement.focus();
          }, 0);
          return;
        }

        // Handle Tab navigation within player
        if (e.shiftKey) {
          // Shift+Tab: If on first element, wrap to last
          if (activeElement === firstElement) {
            e.preventDefault();
            e.stopPropagation();
            lastElement.focus();
          }
        } else {
          // Tab: If on last element, wrap to first
          if (activeElement === lastElement) {
            e.preventDefault();
            e.stopPropagation();
            firstElement.focus();
          }
        }
      }
    };

    if (isActive) {
      // Verwende Capture-Phase für höchste Priorität
      window.addEventListener('keydown', handleKeyDown, true);
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [isActive, getFocusableElements, handleClose]);

  // Auto-focus first element when player becomes active
  useEffect(() => {
    if (isActive && playerContainerRef.current) {
      // Warte kurz, damit der Player gerendert ist
      const timer = setTimeout(() => {
        const focusableElements = getFocusableElements();
        if (focusableElements.length > 0) {
          const activeElement = document.activeElement as HTMLElement;
          const isWithinPlayer = playerContainerRef.current?.contains(activeElement);
          // Nur fokussieren, wenn Focus nicht bereits im Player ist
          if (!isWithinPlayer) {
            focusableElements[0].focus();
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isActive, getFocusableElements]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const texts = textToSpeechPlayerData[language];
  const currentText = segments[currentIndex]?.text || texts.selectText;
  const estimatedDuration = currentText.length / 15; // Rough estimate: 15 chars per second
  const currentTime = (progress / 100) * estimatedDuration;

  if (!isActive) return null;

  return (
    <AnimatePresence>
      <PlayerContainer
        ref={playerContainerRef}
        data-text-to-speech-player="true"
        colors={colors}
        initial={{ opacity: 0, y: 100, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        <PlayerHeader colors={colors}>
          <PlayerTitle>
            🔊 {texts.title}
            <span style={{ fontSize: '0.75rem', opacity: 0.8, marginLeft: '8px' }}>
              ({language === 'de' ? 'DE' : 'EN'})
            </span>
          </PlayerTitle>
          <CloseButton onClick={handleClose} aria-label="Vorlesen schließen">
            ✕
          </CloseButton>
        </PlayerHeader>
        
        <PlayerBody>
          <CurrentText colors={colors}>
            {currentText}
          </CurrentText>
          
          <ProgressContainer>
            <ProgressBar 
              tabIndex={0}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percentage = (x / rect.width) * 100;
                setProgress(percentage);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  // Center click on progress bar
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = rect.width / 2;
                  const percentage = (x / rect.width) * 100;
                  setProgress(percentage);
                }
              }}
              aria-label={texts.progressBar.ariaLabel}
              role={texts.progressBar.role}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <ProgressFill progress={progress} colors={colors} />
            </ProgressBar>
            <TimeDisplay>
              <span>{formatTime(currentTime)}</span>
              <span>{currentIndex + 1} / {segments.length}</span>
              <span>{formatTime(estimatedDuration)}</span>
            </TimeDisplay>
          </ProgressContainer>
          
          <Controls>
            <ControlButton 
              colors={colors} 
              onClick={playPrevious} 
              disabled={currentIndex === 0}
              aria-label={texts.controls.previous}
            >
              ⏮
            </ControlButton>
            <ControlButton 
              colors={colors} 
              size="large" 
              onClick={togglePlayPause}
              aria-label={isPlaying ? texts.controls.pause : texts.controls.play}
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                  <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5v14l11-7z" fill="currentColor"/>
                </svg>
              )}
            </ControlButton>
            <ControlButton 
              colors={colors} 
              onClick={playNext} 
              disabled={currentIndex === segments.length - 1}
              aria-label={texts.controls.next}
            >
              ⏭
            </ControlButton>
          </Controls>
          
          <SettingsRow>
            <SettingLabel>{texts.settings.voice.label}</SettingLabel>
            <VoiceSelect 
              colors={colors}
              value={selectedVoice?.name || ''} 
              onChange={(e) => {
                const voice = voices.find(v => v.name === e.target.value);
                if (voice) setSelectedVoice(voice);
              }}
              aria-label={texts.settings.voice.ariaLabel}
            >
              {voices.map(voice => (
                <option key={voice.name} value={voice.name}>
                  {voice.name.replace('Google ', '').replace('Microsoft ', '')}
                </option>
              ))}
            </VoiceSelect>
          </SettingsRow>
          
          <SettingsRow>
            <SettingLabel>{texts.settings.speed.labelShort}</SettingLabel>
            <Slider
              colors={colors}
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={rate}
              onChange={(e) => setRate(parseFloat(e.target.value))}
              aria-label={texts.settings.speed.ariaLabel}
            />
            <SettingValue>{rate.toFixed(1)}x</SettingValue>
          </SettingsRow>
          
          <SettingsRow>
            <SettingLabel>{texts.settings.volume.label}</SettingLabel>
            <Slider
              colors={colors}
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              aria-label={texts.settings.volume.ariaLabel}
            />
            <SettingValue>{Math.round(volume * 100)}%</SettingValue>
          </SettingsRow>
        </PlayerBody>
      </PlayerContainer>
    </AnimatePresence>
  );
};

export default TextToSpeechPlayer;

